import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trophy, Video, ArrowLeft, Check, AlertCircle, Clock, Gamepad2, Sparkles, Target, Gift, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animated Countdown Component
const SubmitCountdown = ({ targetDate, label, variant = 'default' }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      });
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  const isUrgent = timeLeft.days === 0 && timeLeft.hours < 24;

  return (
    <div className="text-center">
      <p className={`text-sm mb-3 font-medium ${isUrgent ? 'text-red-400' : 'text-yellow-400'}`}>{label}</p>
      <div className="flex justify-center gap-2 sm:gap-3">
        {[
          { value: timeLeft.days, label: 'DAYS' },
          { value: timeLeft.hours, label: 'HRS' },
          { value: timeLeft.minutes, label: 'MIN' },
          { value: timeLeft.seconds, label: 'SEC' }
        ].map((item, idx) => (
          <div key={idx} className={`relative ${variant === 'large' ? 'w-16 sm:w-20' : 'w-14 sm:w-16'}`}>
            <div className={`${isUrgent ? 'bg-red-500/20 border-red-500/50' : 'bg-yellow-500/10 border-yellow-500/30'} border rounded-lg p-2 sm:p-3`}>
              <div className={`text-xl sm:text-2xl font-black ${isUrgent ? 'text-red-400' : 'text-yellow-400'}`} style={{ fontFamily: 'Orbitron, sans-serif' }}>
                {String(item.value).padStart(2, '0')}
              </div>
              <div className="text-[8px] sm:text-[10px] text-gray-500 font-bold tracking-wider">{item.label}</div>
            </div>
            {idx < 3 && (
              <span className={`absolute -right-1.5 sm:-right-2 top-1/2 -translate-y-1/2 text-lg ${isUrgent ? 'text-red-500/50' : 'text-yellow-500/50'}`}>:</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Challenge Card Component
const ChallengeCard = ({ challenge, isSelected, onClick, disabled }) => {
  return (
    <div 
      onClick={disabled ? undefined : onClick}
      className={`relative p-4 rounded-xl border-2 transition-all cursor-pointer overflow-hidden
        ${isSelected 
          ? 'border-green-500 bg-green-500/10 shadow-lg shadow-green-500/20' 
          : disabled 
            ? 'border-gray-800 bg-gray-900/50 opacity-60 cursor-not-allowed'
            : 'border-gray-800 bg-black/40 hover:border-purple-500/50 hover:bg-purple-500/5'
        }`}
    >
      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
          <Check className="w-4 h-4 text-black" />
        </div>
      )}
      
      <div className="flex gap-4">
        {/* Game Cover */}
        {challenge.game_cover_url ? (
          <img 
            src={challenge.game_cover_url} 
            alt={challenge.game_name}
            className="w-16 h-20 object-cover rounded-lg border border-purple-500/30 flex-shrink-0"
          />
        ) : (
          <div className="w-16 h-20 bg-gradient-to-br from-purple-600/30 to-blue-600/30 rounded-lg flex items-center justify-center flex-shrink-0">
            <Gamepad2 className="w-8 h-8 text-purple-400" />
          </div>
        )}
        
        {/* Challenge Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-white text-sm sm:text-base truncate">{challenge.title}</h3>
          {challenge.game_name && (
            <p className="text-xs text-purple-400 flex items-center gap-1 mt-1">
              <Gamepad2 className="w-3 h-3" />
              {challenge.game_name}
            </p>
          )}
          
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
              <Gift className="w-3 h-3" />
              ${challenge.prize_amount}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
              <Trophy className="w-3 h-3" />
              {challenge.max_winners || 1} winner{(challenge.max_winners || 1) > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>
      
      {disabled && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-xl">
          <span className="px-3 py-1 bg-gray-800 text-gray-400 text-xs rounded-full">Already Submitted</span>
        </div>
      )}
    </div>
  );
};

const Submit = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [selectedChallengeId, setSelectedChallengeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasSubmission, setHasSubmission] = useState(false);
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [settings, setSettings] = useState({
    contest_active: true,
    challenge_start_time: null,
    challenge_end_time: null
  });
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [videoType, setVideoType] = useState(null);

  const now = new Date();
  const hasStartedFromSettings = settings.challenge_start_time && new Date(settings.challenge_start_time) <= now;
  const hasEndedFromSettings = settings.challenge_end_time && new Date(settings.challenge_end_time) <= now;
  const hasActiveChallenges = activeChallenges.length > 0;
  
  const hasStarted = hasActiveChallenges || hasStartedFromSettings;
  const hasEnded = !hasActiveChallenges && hasEndedFromSettings;
  // Allow submissions if there are active challenges OR if global contest is active
  const canSubmit = (hasActiveChallenges || settings.contest_active) && hasStarted && !hasEnded;

  const submittedChallengeIds = userSubmissions.map(s => s.challenge_id);
  const availableChallenges = activeChallenges.filter(c => !submittedChallengeIds.includes(c.id));

  useEffect(() => {
    const checkExisting = async () => {
      try {
        // Use /submissions/mine instead of /submissions for faster load
        const [mySubmissionsRes, settingsRes, challengesRes] = await Promise.all([
          axios.get(`${API}/submissions/mine`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/settings`),
          axios.get(`${API}/challenges/active`)
        ]);
        
        const userSubs = mySubmissionsRes.data || [];
        setUserSubmissions(userSubs);
        
        if (userSubs.length > 0) setHasSubmission(true);
        setSettings(settingsRes.data);
        setActiveChallenges(challengesRes.data || []);
        
        const available = challengesRes.data.filter(c => !userSubs.map(s => s.challenge_id).includes(c.id));
        if (available.length > 0) setSelectedChallengeId(available[0].id);
      } catch (error) {
        console.error('Failed to check submission:', error);
      }
    };
    
    if (token) checkExisting();
  }, [token]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const [settingsRes, challengesRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/challenges/active`)
        ]);
        setSettings(settingsRes.data);
        setActiveChallenges(challengesRes.data || []);
      } catch (e) {}
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const url = videoUrl.toLowerCase();
    if (url.includes('twitch.tv') || url.includes('clips.twitch.tv')) {
      setVideoType('twitch');
    } else if (url.includes('youtube.com') || url.includes('youtu.be')) {
      setVideoType('youtube');
    } else if (videoUrl.length > 0) {
      setVideoType('invalid');
    } else {
      setVideoType(null);
    }
  }, [videoUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!videoUrl) { toast.error('Please enter a video URL'); return; }
    if (videoType === 'invalid') { toast.error('Please enter a valid Twitch or YouTube URL'); return; }
    if (!selectedChallengeId) { toast.error('Please select a challenge to submit to'); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/submissions`, {
        video_url: videoUrl,
        video_title: videoTitle,
        challenge_id: selectedChallengeId
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('🎉 Video submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit video');
    } finally {
      setLoading(false);
    }
  };

  // Status screens with improved design
  if (hasSubmission && availableChallenges.length === 0 && activeChallenges.length > 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-950 to-black flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-2xl shadow-green-500/30">
            <Check className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-black text-green-400 mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            All Submitted! 🎉
          </h2>
          <p className="text-gray-400 mb-6">You've entered all active challenges. Good luck!</p>
          
          <div className="text-left p-4 bg-black/50 rounded-xl border border-green-500/30 mb-6 space-y-2">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Your Submissions</p>
            {userSubmissions.map((sub, i) => (
              <div key={i} className="flex items-center gap-3 p-2 bg-green-500/10 rounded-lg">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{sub.challenge_title || 'Challenge'}</p>
                  <p className="text-xs text-gray-500 truncate">{sub.video_title || 'Video submitted'}</p>
                </div>
              </div>
            ))}
          </div>
          
          <Button onClick={() => navigate('/dashboard')} className="bg-green-600 hover:bg-green-700 px-8">
            View Dashboard <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  if (!hasStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-950 to-black flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center shadow-2xl shadow-yellow-500/30 animate-pulse">
            <Clock className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-black text-yellow-400 mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            Coming Soon!
          </h2>
          <p className="text-gray-400 mb-6">
            {settings.challenge_start_time 
              ? "Get ready! Submissions open soon."
              : "No active challenges. Check back later!"}
          </p>
          {settings.challenge_start_time && (
            <div className="mb-6">
              <SubmitCountdown targetDate={settings.challenge_start_time} label="Submissions open in:" variant="large" />
            </div>
          )}
          <Button onClick={() => navigate('/')} variant="outline" className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </div>
    );
  }

  if (hasEnded) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-950 to-black flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center shadow-2xl shadow-red-500/30">
            <AlertCircle className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-black text-red-400 mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            Submissions Closed
          </h2>
          <p className="text-gray-400 mb-6">The submission period has ended. Winners will be announced soon!</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-red-600 hover:bg-red-700">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (!settings.contest_active && !hasActiveChallenges) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-950 to-black flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-full flex items-center justify-center shadow-2xl shadow-yellow-500/30">
            <AlertCircle className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-black text-yellow-400 mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            No Active Challenges
          </h2>
          <p className="text-gray-400 mb-6">There are no active challenges at the moment. Check back soon!</p>
          <Button onClick={() => navigate('/')} variant="outline" className="border-yellow-500/50">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  // Main submission form
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-950 to-black">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 border-b border-red-900/30 bg-black/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <Trophy className="w-5 sm:w-6 h-5 sm:h-6 text-red-500" />
              <span className="font-bold text-sm sm:text-lg tracking-wider uppercase text-red-500" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                CesarM Challenges
              </span>
            </div>
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="px-2 sm:px-4">
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Dashboard</span>
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative pt-24 pb-12 px-3 sm:px-4 max-w-4xl mx-auto">
        
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/20 to-red-500/20 rounded-full border border-purple-500/30 mb-4">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-purple-300">Submit your best gameplay</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-3" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            Enter the <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-purple-500">Challenge</span>
          </h1>
          <p className="text-gray-400 max-w-md mx-auto">
            Share your epic gaming moments for a chance to win amazing prizes!
          </p>
        </div>

        {/* Dynamic Countdown Banner - Shows selected challenge's end time */}
        {(() => {
          const selectedChallenge = activeChallenges.find(c => c.id === selectedChallengeId);
          const endTime = selectedChallenge?.end_time || settings.challenge_end_time;
          const challengeTitle = selectedChallenge?.title;
          
          if (!endTime || hasEnded) return null;
          
          return (
            <div className="mb-8 p-4 sm:p-6 rounded-2xl bg-gradient-to-r from-red-500/10 via-purple-500/10 to-red-500/10 border border-red-500/30">
              <SubmitCountdown 
                targetDate={endTime} 
                label={challengeTitle ? `⏰ "${challengeTitle}" closes in:` : "⏰ Submissions close in:"} 
              />
            </div>
          );
        })()}

        <div className="grid lg:grid-cols-5 gap-6">
          
          {/* Challenge Selection - Left Side */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-purple-400" />
              <h2 className="font-bold text-white">Select Challenge</h2>
            </div>
            
            {activeChallenges.length === 0 ? (
              <div className="p-6 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-center">
                <AlertCircle className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
                <p className="text-yellow-400 font-medium">No active challenges</p>
                <p className="text-gray-500 text-sm mt-1">Check back later!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activeChallenges.map((challenge) => (
                  <ChallengeCard
                    key={challenge.id}
                    challenge={challenge}
                    isSelected={selectedChallengeId === challenge.id}
                    onClick={() => setSelectedChallengeId(challenge.id)}
                    disabled={submittedChallengeIds.includes(challenge.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Submission Form - Right Side */}
          <div className="lg:col-span-3">
            <Card className="bg-black/40 border-gray-800 backdrop-blur">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-white">
                  <Video className="w-5 h-5 text-red-500" />
                  Submit Your Video
                </CardTitle>
                <CardDescription>
                  Paste your Twitch video or YouTube video link below
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                  
                  {/* Video URL Input */}
                  <div className="space-y-2">
                    <Label htmlFor="videoUrl" className="text-gray-300">Video URL *</Label>
                    <div className="relative">
                      <Input
                        id="videoUrl"
                        type="url"
                        placeholder="https://twitch.tv/videos/... or https://youtube.com/..."
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        className="bg-black/50 border-gray-700 focus:border-purple-500 pr-24 h-12"
                        data-testid="video-url-input"
                        required
                      />
                      {videoType && videoType !== 'invalid' && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            videoType === 'twitch' ? 'bg-purple-500/20 text-purple-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {videoType === 'twitch' ? '📺 Twitch' : '▶️ YouTube'}
                          </span>
                        </div>
                      )}
                    </div>
                    {videoType === 'invalid' && (
                      <p className="text-yellow-400 text-sm flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        Please enter a valid Twitch or YouTube URL
                      </p>
                    )}
                  </div>

                  {/* Video Title Input */}
                  <div className="space-y-2">
                    <Label htmlFor="videoTitle" className="text-gray-300">Video Title (Optional)</Label>
                    <Input
                      id="videoTitle"
                      type="text"
                      placeholder="My Epic Gaming Moment..."
                      value={videoTitle}
                      onChange={(e) => setVideoTitle(e.target.value)}
                      className="bg-black/50 border-gray-700 focus:border-purple-500 h-12"
                      data-testid="video-title-input"
                      maxLength={100}
                    />
                  </div>

                  {/* Platform Icons */}
                  <div className="flex items-center gap-4 p-3 rounded-lg bg-gray-900/50 border border-gray-800">
                    <span className="text-xs text-gray-500">Supported:</span>
                    <div className="flex items-center gap-1 text-purple-400">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                      </svg>
                      <span className="text-xs">Twitch</span>
                    </div>
                    <div className="flex items-center gap-1 text-red-400">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                      <span className="text-xs">YouTube</span>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    className="w-full h-14 text-lg font-bold bg-gradient-to-r from-red-600 to-purple-600 hover:from-red-500 hover:to-purple-500 transition-all"
                    disabled={loading || videoType === 'invalid' || !videoUrl || !selectedChallengeId || availableChallenges.length === 0}
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Zap className="w-5 h-5" />
                        Submit Entry
                      </span>
                    )}
                  </Button>

                  {/* Selected Challenge Info */}
                  {selectedChallengeId && availableChallenges.find(c => c.id === selectedChallengeId) && (
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                      <p className="text-sm text-green-400 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        Submitting to: <span className="font-bold">{availableChallenges.find(c => c.id === selectedChallengeId)?.title}</span>
                      </p>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Submit;
